import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Image,
  Text,
  IconButton,
  Progress,
  useToast,
  SimpleGrid,
  Center,
  Icon,
  useColorModeValue,
  AspectRatio,
  Spinner,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { FiUploadCloud } from 'react-icons/fi';
import { Api } from 'api';

interface ImageUploaderProps {
  value: string[];
  onChange: (urls: string[]) => void;
  maxImages?: number;
}

interface UploadingImage {
  id: string;
  file: File;
  preview: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  url?: string;
  error?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ value = [], onChange, maxImages = 5 }) => {
  const [uploadingImages, setUploadingImages] = useState<UploadingImage[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // Dark mode colors
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const dragBgColor = useColorModeValue('blue.50', 'blue.900');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.700');

  const totalImages = value.length + uploadingImages.filter((img) => img.status === 'uploading').length;
  const canAddMore = totalImages < maxImages;

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const uploadFiles = async (files: File[]) => {
    const newUploadingImages: UploadingImage[] = files.map((file) => ({
      id: Math.random().toString(36).substring(2, 11),
      file,
      preview: URL.createObjectURL(file),
      progress: 0,
      status: 'uploading' as const,
    }));

    setUploadingImages((prev) => [...prev, ...newUploadingImages]);

    const uploadedUrls: string[] = [];
    const completedIds: string[] = [];
    const failedIds: string[] = [];

    // Process uploads sequentially to avoid race conditions
    const uploadPromises = newUploadingImages.map(async (uploadingImage) => {
      try {
        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setUploadingImages((prev) =>
            prev.map((img) =>
              img.id === uploadingImage.id && img.progress < 90 ? { ...img, progress: img.progress + 10 } : img,
            ),
          );
        }, 200);

        const response = await Api.Product.uploadPhoto(uploadingImage.file);
        clearInterval(progressInterval);

        if (response.data) {
          // Mark as completed with 100% progress
          setUploadingImages((prev) =>
            prev.map((img) =>
              img.id === uploadingImage.id ? { ...img, progress: 100, status: 'success', url: response.data } : img,
            ),
          );

          // Collect the URL and ID
          uploadedUrls.push(response.data);
          completedIds.push(uploadingImage.id);

          // Clean up preview URL immediately
          URL.revokeObjectURL(uploadingImage.preview);
        }
      } catch (error) {
        console.error('Upload error:', error);
        setUploadingImages((prev) =>
          prev.map((img) =>
            img.id === uploadingImage.id ? { ...img, status: 'error', error: 'Yükleme başarısız' } : img,
          ),
        );

        failedIds.push(uploadingImage.id);

        toast({
          title: 'Yükleme Hatası',
          description: `${uploadingImage.file.name} yüklenemedi.`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });

        // Clean up preview URL
        URL.revokeObjectURL(uploadingImage.preview);
      }
    });

    await Promise.all(uploadPromises);

    // Update all URLs at once and remove all completed/failed uploads immediately
    if (uploadedUrls.length > 0) {
      onChange([...value, ...uploadedUrls]);
    }

    // Remove all completed and failed uploads at once
    setUploadingImages((prev) => prev.filter((img) => !completedIds.includes(img.id) && !failedIds.includes(img.id)));
  };

  const handleFiles = async (files: FileList) => {
    const validFiles: File[] = [];
    const remainingSlots = maxImages - totalImages;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        validFiles.push(file);
      } else {
        toast({
          title: 'Geçersiz Dosya',
          description: `${file.name} bir resim dosyası değil.`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }

    if (validFiles.length > 0) {
      uploadFiles(validFiles);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (!canAddMore) {
        toast({
          title: 'Limit Aşıldı',
          description: `En fazla ${maxImages} fotoğraf yükleyebilirsiniz.`,
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [canAddMore, maxImages, toast],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeImage = (index: number) => {
    const newUrls = value.filter((_, i) => i !== index);
    onChange(newUrls);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    const newUrls = [...value];
    const [removed] = newUrls.splice(fromIndex, 1);
    newUrls.splice(toIndex, 0, removed);
    onChange(newUrls);
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Upload Area */}
      {canAddMore && (
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          cursor="pointer"
          borderWidth={2}
          borderStyle="dashed"
          borderColor={dragActive ? 'blue.400' : borderColor}
          borderRadius="lg"
          p={8}
          bg={dragActive ? dragBgColor : bgColor}
          transition="all 0.2s"
          _hover={{ borderColor: 'blue.400', bg: dragBgColor }}
        >
          <VStack spacing={3}>
            <Icon as={FiUploadCloud} boxSize={10} color={textColor} />
            <Text fontSize="lg" fontWeight="medium" textAlign="center">
              Fotoğraf yüklemek için tıklayın veya sürükleyin
            </Text>
            <Text fontSize="sm" color={textColor} textAlign="center">
              PNG, JPG, JPEG (Maks. {maxImages} fotoğraf)
            </Text>
            <Text fontSize="xs" color={textColor}>
              {totalImages} / {maxImages} fotoğraf yüklendi
            </Text>
          </VStack>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
      )}

      {/* Images Grid */}
      {(value.length > 0 || uploadingImages.length > 0) && (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
          {/* Existing Images */}
          {value.map((url, index) => (
            <Box key={url} position="relative" borderRadius="lg" overflow="hidden" bg={cardBg} shadow="sm">
              <AspectRatio ratio={1}>
                <Image src={url} alt={`Product ${index + 1}`} objectFit="cover" />
              </AspectRatio>
              <HStack position="absolute" top={2} right={2} spacing={1}>
                {index > 0 && (
                  <IconButton
                    aria-label="Öne taşı"
                    icon={<Text fontSize="xs">←</Text>}
                    size="xs"
                    colorScheme="blue"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveImage(index, index - 1);
                    }}
                  />
                )}
                {index < value.length - 1 && (
                  <IconButton
                    aria-label="Arkaya taşı"
                    icon={<Text fontSize="xs">→</Text>}
                    size="xs"
                    colorScheme="blue"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveImage(index, index + 1);
                    }}
                  />
                )}
                <IconButton
                  aria-label="Sil"
                  icon={<DeleteIcon />}
                  size="xs"
                  colorScheme="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeImage(index);
                  }}
                />
              </HStack>
              {index === 0 && (
                <Box position="absolute" bottom={0} left={0} right={0} bg="blue.500" color="white" px={2} py={1}>
                  <Text fontSize="xs" textAlign="center">
                    Ana Görsel
                  </Text>
                </Box>
              )}
            </Box>
          ))}

          {/* Uploading Images */}
          {uploadingImages.map((img) => (
            <Box key={img.id} position="relative" borderRadius="lg" overflow="hidden" bg={cardBg} shadow="sm">
              <AspectRatio ratio={1}>
                <Box>
                  <Image src={img.preview} alt="Uploading" objectFit="cover" opacity={0.7} />
                  {img.status === 'uploading' && (
                    <Center position="absolute" inset={0} bg="blackAlpha.600">
                      <VStack spacing={2}>
                        <Spinner size="lg" color="white" />
                        <Text fontSize="xs" color="white">
                          {img.progress}%
                        </Text>
                      </VStack>
                    </Center>
                  )}
                  {img.status === 'error' && (
                    <Center position="absolute" inset={0} bg="red.600" opacity={0.9}>
                      <VStack spacing={1}>
                        <Text fontSize="xs" color="white" textAlign="center">
                          Yükleme başarısız
                        </Text>
                      </VStack>
                    </Center>
                  )}
                </Box>
              </AspectRatio>
              {img.status === 'uploading' && (
                <Progress
                  value={img.progress}
                  size="xs"
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  colorScheme="blue"
                />
              )}
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Helper Text */}
      {value.length === 0 && uploadingImages.length === 0 && (
        <Text fontSize="sm" color={textColor} textAlign="center">
          Henüz fotoğraf yüklenmedi
        </Text>
      )}
    </VStack>
  );
};
